import { Fragment, useCallback, useEffect, useState } from "react";

import Icon from "../Icon";
import Button from "../Button";
import AutocompleteField from "./AutocompleteField";
import TextField from "./TextField";
import CheckBox from "./CheckBox";
import Radio from "./Radio";
import Typography from "@mui/material/Typography";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import noop from "lodash/noop";
import NumberField from "./NumberField";
import DateTimeField from "./DateTimeField";
import "./style.scss";

export enum fieldTypes {
  TEXT,
  AUTO_COMPLETE,
  SELECT,
  FILE,
  CHECKBOX,
  RADIO,
  NUMBER,
  DATETIME,
}

const componentMapper = {
  [fieldTypes.TEXT.toString()]: TextField,
  [fieldTypes.AUTO_COMPLETE.toString()]: AutocompleteField,
  [fieldTypes.SELECT.toString()]: AutocompleteField,
  [fieldTypes.CHECKBOX.toString()]: CheckBox,
  [fieldTypes.RADIO.toString()]: Radio,
  [fieldTypes.NUMBER.toString()]: NumberField,
  [fieldTypes.DATETIME.toString()]: DateTimeField,
};

interface Option {
  label: string;
  value: string | boolean | number;
  group?: string;
}

export interface FormFieldInterface {
  key: string;
  label: string;
  type: fieldTypes;
  helperText?: string;
  placeholder?: string;
  multiline?: boolean;
  options?: Array<Option>;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  selectFirstByDefault?: boolean;
}

interface FormProps {
  title?: string;
  description?: string;

  formFields: FormFieldInterface[];

  onEditFieldPress?: Function;
  onDeleteFieldPress?: Function;

  isCustomizable?: boolean;
  hideSubmitButton?: boolean;

  // since this could be for edit only as well
  onSubmit?: Function;

  defaultFormValues?: object;
}

const Form: React.FC<FormProps> = ({
  title,
  description,
  defaultFormValues,
  isCustomizable = false,
  hideSubmitButton = false,
  formFields,
  onEditFieldPress = noop,
  onDeleteFieldPress = noop,
  onSubmit = noop,
}) => {
  const [formValues, setFormValues] = useState<{ [key: string]: any }>({});

  useEffect(() => {
    !isEmpty(defaultFormValues) && setFormValues(defaultFormValues || {});
  }, [defaultFormValues]);

  const handleEditFieldPress =
    (key: string, fieldInfo: FormFieldInterface) => () => {
      onEditFieldPress({ ...fieldInfo, key });
    };

  const handleDeleteFieldPress = (key: string) => () => {
    onDeleteFieldPress(key);
  };

  const handleValueChange = useCallback(
    (key: string, value: any) =>
      setFormValues((prevState) => ({
        ...prevState,
        [key]: value,
      })),
    [],
  );

  const handleFormSubmit = (e: any) => {
    e.preventDefault();
    onSubmit(formValues);
  };

  return (
    <form id="form-container" onSubmit={handleFormSubmit}>
      {(!!title || !!description) && (
        <div className="info-container">
          {!!title && <Typography variant="h5">{title}</Typography>}
          {!!description && (
            <Typography variant="caption">{description}</Typography>
          )}
        </div>
      )}

      {map(formFields, (singleField) => {
        const {
          key,
          placeholder,
          label,
          multiline,
          options,
          multiple,
          selectFirstByDefault,
          required,
          disabled,
          helperText,
          type,
        } = singleField;
        const FieldComp: any = componentMapper[type.toString()];

        return (
          <Fragment key={key}>
            {isCustomizable && (
              <div className="customization-icons-container">
                <Icon
                  name={"edit"}
                  onClick={handleEditFieldPress(String(key), singleField)}
                />
                <Icon
                  name={"trash"}
                  color={"error"}
                  onClick={handleDeleteFieldPress(String(key))}
                />
              </div>
            )}
            <FieldComp
              fieldKey={key}
              helperText={helperText}
              disabled={disabled}
              required={required}
              value={formValues[key]}
              onChange={handleValueChange}
              multiline={multiline}
              placeholder={placeholder}
              label={label}
              // non-common
              options={options}
              // for file
              multiple={multiple}
              selectFirstByDefault={selectFirstByDefault}
            />
            <br />
          </Fragment>
        );
      })}

      {!hideSubmitButton && (
        <Button className="submit-button" type="submit">
          SUBMIT
        </Button>
      )}
    </form>
  );
};

export default Form;
